import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Button, Col, Row } from 'reactstrap';
import { DatePicker } from 'antd';
import moment from 'moment';
import { default as ReactSelect } from 'react-select';
import Swal from 'sweetalert2';
import Sidebar from '../../Sidebar';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import FloatingControls from '../../components/FloatingControls';
import ListPageGradientSummary from '../../components/ListPageGradientSummary';
import ReportExportDropdown from '../../components/ReportExportDropdown';
import { buildGradientCard } from '../../utils/listSummaryUtils';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsExcel } from '../../utils/ExcelExporter';
import { displayError } from '../../helpers';
import { getAllEquipments } from '../../services/EquipmentServices';
import {
  correctCheckInKm,
  getCheckInKmCorrectionList,
} from '../../services/CheckInServices';
import '../../App.css';

const { RangePicker } = DatePicker;

const EXPORT_COLUMNS = [
  { fieldName: 'checkout_no', headerName: 'Checkout No' },
  { fieldName: 'plant_name', headerName: 'Plant' },
  { fieldName: 'movement_type_label', headerName: 'Type' },
  { fieldName: 'dc_no', headerName: 'DC No' },
  { fieldName: 'dc_date', headerName: 'DC Date' },
  { fieldName: 'time', headerName: 'Time' },
  { fieldName: 'customer_name', headerName: 'Customer' },
  { fieldName: 'site_name', headerName: 'Site' },
  { fieldName: 'grade', headerName: 'Grade' },
  { fieldName: 'qty', headerName: 'Qty' },
  { fieldName: 'opening_km', headerName: 'Open KM' },
  { fieldName: 'ending_km', headerName: 'End KM' },
  { fieldName: 'total_km', headerName: 'Total KM' },
  { fieldName: 'opening_hm', headerName: 'Open HM' },
  { fieldName: 'ending_hm', headerName: 'End HM' },
  { fieldName: 'total_hm', headerName: 'Total HM' },
];

function CheckInKmCorrection() {
  const [cookies] = useCookies(['myToken']);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [dateRange, setDateRange] = useState([
    moment().startOf('month'),
    moment(),
  ]);
  const [rows, setRows] = useState([]);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    getAllEquipments(cookies)
      .then((response) => {
        const options = (response?.equipment_list || [])
          .filter((item) => item.status !== false && item.status !== 0)
          .map((item) => ({
            value: item.id,
            label: item.equip_name,
          }))
          .sort((a, b) => String(a.label).localeCompare(String(b.label)));
        setVehicleOptions(options);
      })
      .catch((error) => {
        displayError(error.response?.data ?? error.message, 'Failed to load vehicles');
      });
  }, [cookies]);

  const loadList = useCallback(() => {
    if (!selectedVehicle?.value) {
      Swal.fire('Select vehicle', 'Please select a vehicle.', 'warning');
      return;
    }
    if (!dateRange?.[0] || !dateRange?.[1]) {
      Swal.fire('Select dates', 'Please select a date range.', 'warning');
      return;
    }

    setIsLoading(true);
    getCheckInKmCorrectionList(cookies, {
      vehicle_id: selectedVehicle.value,
      from_date: dateRange[0].format('DD-MM-YYYY'),
      upto_date: dateRange[1].format('DD-MM-YYYY'),
    })
      .then((response) => {
        const list = (response?.km_correction_list || []).map((row) => ({
          ...row,
          edit_opening_km: row.opening_km ?? 0,
          edit_ending_km: row.ending_km ?? 0,
          edit_opening_hm: row.opening_hm ?? 0,
          edit_ending_hm: row.ending_hm ?? 0,
        }));
        setRows(list);
        setVehicleInfo(response?.vehicle || null);
      })
      .catch((error) => {
        setRows([]);
        setVehicleInfo(null);
        displayError(error.response?.data ?? error.message, 'Failed to load trips');
      })
      .finally(() => setIsLoading(false));
  }, [cookies, selectedVehicle, dateRange]);

  const handleFieldChange = (gatePassId, field, value) => {
    setRows((current) =>
      current.map((row) =>
        row.gate_pass_id === gatePassId
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  const previewTotalKm = (row) => {
    const opening = Number(row.edit_opening_km) || 0;
    const ending = Number(row.edit_ending_km) || 0;
    if (ending <= 0) return 0;
    return Math.max(0, ending - opening);
  };

  const previewTotalHm = (row) => {
    const opening = Number(row.edit_opening_hm) || 0;
    const ending = Number(row.edit_ending_hm) || 0;
    if (ending <= 0) return 0;
    return Math.max(0, ending - opening);
  };

  const handleSaveRow = async (row) => {
    if (row.is_locked) {
      Swal.fire('Locked', 'This trip is invoiced and cannot be corrected.', 'info');
      return;
    }

    const openingKm = Number(row.edit_opening_km);
    const endingKm = Number(row.edit_ending_km);
    const openingHm = Number(row.edit_opening_hm);
    const endingHm = Number(row.edit_ending_hm);
    if ([openingKm, endingKm, openingHm, endingHm].some((value) => Number.isNaN(value))) {
      Swal.fire('Invalid', 'KM and HM values must be numbers.', 'warning');
      return;
    }
    if (endingKm > 0 && endingKm <= openingKm) {
      Swal.fire(
        'Invalid',
        `Ending KM must be greater than opening KM (${openingKm}).`,
        'warning'
      );
      return;
    }
    if (endingHm > 0 && endingHm < openingHm) {
      Swal.fire(
        'Invalid',
        `Ending HM must be greater than or equal to opening HM (${openingHm}).`,
        'warning'
      );
      return;
    }

    const confirm = await Swal.fire({
      title: 'Save KM / HM correction?',
      html:
        `Open KM <b>${row.opening_km}</b> → <b>${openingKm}</b><br/>`
        + `End KM <b>${row.ending_km}</b> → <b>${endingKm}</b><br/>`
        + `Open HM <b>${row.opening_hm}</b> → <b>${openingHm}</b><br/>`
        + `End HM <b>${row.ending_hm}</b> → <b>${endingHm}</b>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Save',
    });
    if (!confirm.isConfirmed) return;

    setSavingId(row.gate_pass_id);
    try {
      const response = await correctCheckInKm(cookies, {
        gate_pass_id: row.gate_pass_id,
        opening_km: openingKm,
        ending_km: endingKm,
        opening_hm: openingHm,
        ending_hm: endingHm,
      });
      setRows((current) =>
        current.map((item) =>
          item.gate_pass_id === row.gate_pass_id
            ? {
                ...item,
                opening_km: response.opening_km,
                ending_km: response.ending_km,
                total_km: response.total_km,
                opening_hm: response.opening_hm,
                ending_hm: response.ending_hm,
                total_hm: response.total_hm,
                edit_opening_km: response.opening_km,
                edit_ending_km: response.ending_km,
                edit_opening_hm: response.opening_hm,
                edit_ending_hm: response.ending_hm,
                editing_details: response.editing_details || item.editing_details,
              }
            : item
        )
      );
      if (response.vehicle_co_open_km != null || response.vehicle_co_open_hm != null) {
        setVehicleInfo((current) =>
          current
            ? {
                ...current,
                co_open_km: response.vehicle_co_open_km ?? current.co_open_km,
                co_open_hm: response.vehicle_co_open_hm ?? current.co_open_hm,
              }
            : current
        );
      }
      Swal.fire('Saved', 'KM / HM updated and vehicle meters refreshed.', 'success');
    } catch (error) {
      displayError(error.response?.data ?? error.message, 'Failed to save KM');
    } finally {
      setSavingId(null);
    }
  };

  const dirtyCount = useMemo(
    () =>
      rows.filter(
        (row) =>
          Number(row.edit_opening_km) !== Number(row.opening_km)
          || Number(row.edit_ending_km) !== Number(row.ending_km)
          || Number(row.edit_opening_hm) !== Number(row.opening_hm)
          || Number(row.edit_ending_hm) !== Number(row.ending_hm)
      ).length,
    [rows]
  );

  const highlightCard = useMemo(() => {
    const totalKm = rows.reduce((sum, row) => sum + previewTotalKm(row), 0);
    return buildGradientCard('Total KM', totalKm, 'today');
  }, [rows]);

  const summaryCards = useMemo(() => {
    const dcCount = rows.filter((row) => row.movement_type !== 'maintenance').length;
    const maintCount = rows.filter((row) => row.movement_type === 'maintenance').length;
    const plantCount = new Set(
      rows.map((row) => row.plant_name).filter(Boolean)
    ).size;
    const totalHm = rows.reduce((sum, row) => sum + previewTotalHm(row), 0);
    const totalQty = rows.reduce((sum, row) => {
      const qty = Number(row.qty);
      return sum + (Number.isNaN(qty) ? 0 : qty);
    }, 0);
    return [
      buildGradientCard('Total Trips', rows.length, 'blue'),
      buildGradientCard('DC Trips', dcCount, 'teal'),
      buildGradientCard('Maintenance', maintCount, 'purple'),
      buildGradientCard('Plants', plantCount, 'red'),
      buildGradientCard('Total Qty', Number(totalQty.toFixed(2)), 'teal'),
      buildGradientCard('Total HM', totalHm, 'blue'),
    ];
  }, [rows]);

  const exportFileBase = useMemo(() => {
    const vehiclePart = String(selectedVehicle?.label || 'vehicle')
      .replace(/[^\w.-]+/g, '_')
      .slice(0, 40);
    const from = dateRange?.[0]?.format('DD-MM-YYYY') || '';
    const upto = dateRange?.[1]?.format('DD-MM-YYYY') || '';
    return `checkin_km_correction_${vehiclePart}_${from}_to_${upto}`;
  }, [selectedVehicle, dateRange]);

  const exportRecords = useMemo(
    () =>
      rows.map((row) => ({
        checkout_no: row.checkout_no,
        plant_name: row.plant_name,
        movement_type_label:
          row.movement_type === 'maintenance' ? 'Maintenance' : (row.movement_type_label || 'DC'),
        dc_no: row.dc_no,
        dc_date: row.dc_date,
        time: row.time,
        customer_name: row.customer_name,
        site_name: row.site_name,
        grade: row.grade,
        qty: row.qty,
        opening_km: Number(row.edit_opening_km) || 0,
        ending_km: Number(row.edit_ending_km) || 0,
        total_km: previewTotalKm(row),
        opening_hm: Number(row.edit_opening_hm) || 0,
        ending_hm: Number(row.edit_ending_hm) || 0,
        total_hm: previewTotalHm(row),
      })),
    [rows]
  );

  const ensureExportData = () => {
    if (!exportRecords.length) {
      Swal.fire('No data', 'Load trips first, then download.', 'info');
      return false;
    }
    return true;
  };

  const handleCSVExport = () => {
    if (!ensureExportData()) return;
    exportAsCSV(EXPORT_COLUMNS, exportRecords, exportFileBase);
  };

  const handleExcelExport = () => {
    if (!ensureExportData()) return;
    exportAsExcel(EXPORT_COLUMNS, exportRecords, exportFileBase);
  };

  return (
    <div id="outer-container" className="App">
      <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
      <div id="page-wrap">
        <FloatingControls
          showSave={false}
          homeLink="/Home"
          tableLink="/CheckInTable"
          formLink="/CheckIn"
          addNewLabel="New Check In"
        />
        <div className="container-fluid px-3" style={{ maxWidth: 1600 }}>
          <PremiumPageHeader
            module="GATE"
            title="Check-In KM Correction"
            subtitle="Select one vehicle and date range to track all plant and maintenance trips, then correct KM / HM."
            modeLabel="CORRECTION"
            headerBackground="linear-gradient(135deg, #ecfeff 0%, #f8fafc 55%, #ffffff 100%)"
          />

          <ListPageGradientSummary highlightCard={highlightCard} cards={summaryCards} />

          <div
            className="p-3 mb-3"
            style={{
              background: '#fff',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
            }}
          >
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <label className="form-label fw-bold mb-1">Vehicle</label>
                <ReactSelect
                  className="react-select-dropdown"
                  classNamePrefix="react-select"
                  options={vehicleOptions}
                  value={selectedVehicle}
                  onChange={setSelectedVehicle}
                  placeholder="Type vehicle no. to search..."
                  isClearable
                  isSearchable
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  filterOption={(option, rawInput) => {
                    const input = String(rawInput || '').trim().toLowerCase();
                    if (!input) return true;
                    return String(option.label || '').toLowerCase().includes(input);
                  }}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue
                      ? `No vehicle matching "${inputValue}"`
                      : 'No vehicles available'
                  }
                />
              </Col>
              <Col md={4}>
                <label className="form-label fw-bold mb-1">Date range</label>
                <RangePicker
                  className="w-100"
                  format="DD-MM-YYYY"
                  value={dateRange}
                  onChange={(value) => setDateRange(value)}
                  allowClear={false}
                />
              </Col>
              <Col md={4} className="d-flex gap-2 align-items-center flex-wrap">
                <Button
                  color="primary"
                  className="fw-bold"
                  onClick={loadList}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Show trips'}
                </Button>
                <ReportExportDropdown
                  onExcel={handleExcelExport}
                  onCSV={handleCSVExport}
                  disabled={isLoading || !rows.length}
                />
              </Col>
            </Row>

            {vehicleInfo && (
              <div className="mt-3 small text-muted">
                Vehicle <strong>{vehicleInfo.equip_name}</strong>
                {' · '}All plants + maintenance in range
                {' · '}Base meter {vehicleInfo.meter_reading ?? 0}
                {' · '}Current co-open KM <strong>{vehicleInfo.co_open_km ?? 0}</strong>
                {dirtyCount > 0 ? ` · ${dirtyCount} unsaved change(s)` : ''}
              </div>
            )}
          </div>

          <style>{`
            .km-correction-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0;
              font-size: 12px;
              table-layout: auto;
            }
            .km-correction-table th,
            .km-correction-table td {
              vertical-align: middle !important;
              white-space: nowrap;
              padding: 6px 8px !important;
              line-height: 1.2;
              height: 42px;
            }
            .km-correction-table th {
              background: #f1f5f9;
              font-weight: 700;
              position: sticky;
              top: 0;
              z-index: 1;
            }
            .km-correction-table .km-input {
              width: 88px;
              height: 30px;
              padding: 2px 6px;
              margin: 0;
              display: inline-block;
              vertical-align: middle;
              line-height: 1.2;
              box-sizing: border-box;
            }
            .km-correction-table .km-input::-webkit-outer-spin-button,
            .km-correction-table .km-input::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
            .km-correction-table .km-input[type=number] {
              -moz-appearance: textfield;
              appearance: textfield;
            }
            .km-correction-table .km-total {
              display: inline-block;
              min-width: 36px;
              text-align: right;
              font-weight: 700;
              vertical-align: middle;
            }
            .km-correction-table .btn {
              height: 30px;
              padding: 0 10px;
              line-height: 28px;
              vertical-align: middle;
            }
          `}</style>

          <div
            className="table-responsive"
            style={{
              background: '#fff',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              maxHeight: '70vh',
              overflow: 'auto',
            }}
          >
            <table className="table table-sm table-hover mb-0 km-correction-table">
              <thead>
                <tr>
                  <th>Checkout No</th>
                  <th>Plant</th>
                  <th>Type</th>
                  <th>DC No</th>
                  <th>DC Date</th>
                  <th>Time</th>
                  <th>Customer</th>
                  <th>Site</th>
                  <th>Grade</th>
                  <th>Qty</th>
                  <th>Open KM</th>
                  <th>End KM</th>
                  <th>Total KM</th>
                  <th>Open HM</th>
                  <th>End HM</th>
                  <th>Total HM</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {!isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={17} className="text-center text-muted py-4">
                      No trips found. Select vehicle and date range, then click Show trips.
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const changed =
                    Number(row.edit_opening_km) !== Number(row.opening_km)
                    || Number(row.edit_ending_km) !== Number(row.ending_km)
                    || Number(row.edit_opening_hm) !== Number(row.opening_hm)
                    || Number(row.edit_ending_hm) !== Number(row.ending_hm);
                  const typeLabel = row.movement_type === 'maintenance' ? 'Maint' : 'DC';
                  return (
                    <tr
                      key={row.gate_pass_id}
                      style={{ background: changed ? '#fff7ed' : undefined }}
                    >
                      <td>{row.checkout_no || '-'}</td>
                      <td>{row.plant_name || '-'}</td>
                      <td>{typeLabel}</td>
                      <td>{row.dc_no || '-'}</td>
                      <td>{row.dc_date || '-'}</td>
                      <td>{row.time || '-'}</td>
                      <td title={row.customer_name || ''}>{row.customer_name || '-'}</td>
                      <td title={row.site_name || ''}>{row.site_name || '-'}</td>
                      <td>{row.grade || '-'}</td>
                      <td>{row.qty === '' || row.qty == null ? '-' : row.qty}</td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm km-input"
                          value={row.edit_opening_km}
                          disabled={row.is_locked || savingId === row.gate_pass_id}
                          onChange={(event) =>
                            handleFieldChange(
                              row.gate_pass_id,
                              'edit_opening_km',
                              event.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm km-input"
                          value={row.edit_ending_km}
                          disabled={row.is_locked || savingId === row.gate_pass_id}
                          onChange={(event) =>
                            handleFieldChange(
                              row.gate_pass_id,
                              'edit_ending_km',
                              event.target.value
                            )
                          }
                        />
                      </td>
                      <td><span className="km-total">{previewTotalKm(row)}</span></td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm km-input"
                          value={row.edit_opening_hm}
                          disabled={row.is_locked || savingId === row.gate_pass_id}
                          onChange={(event) =>
                            handleFieldChange(
                              row.gate_pass_id,
                              'edit_opening_hm',
                              event.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm km-input"
                          value={row.edit_ending_hm}
                          disabled={row.is_locked || savingId === row.gate_pass_id}
                          onChange={(event) =>
                            handleFieldChange(
                              row.gate_pass_id,
                              'edit_ending_hm',
                              event.target.value
                            )
                          }
                        />
                      </td>
                      <td><span className="km-total">{previewTotalHm(row)}</span></td>
                      <td>
                        <Button
                          color="success"
                          size="sm"
                          className="fw-bold"
                          disabled={
                            row.is_locked
                            || !changed
                            || savingId === row.gate_pass_id
                          }
                          onClick={() => handleSaveRow(row)}
                        >
                          {savingId === row.gate_pass_id ? '...' : 'Save'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckInKmCorrection;
